import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { MdClose } from "react-icons/md";
import { BsFillInboxesFill } from "react-icons/bs";
import { PartConfirmationBucketEntry } from "./utils/partsCalculationUtils";
import { PartConfirmationSendResult } from "../../views/Orders/hooks/useSendPartConfirmationBucket";
import { FiSend } from "react-icons/fi";

interface PartConfirmationBucketPanelProps {
  partConfirmationBucket: PartConfirmationBucketEntry[];
  onRemoveSupplier: (supplierId: string) => void;
  onSend: () => void;
  isSending: boolean;
  isSendEnabled?: boolean;
  sendDisabledReason?: string;
  sendResults?: PartConfirmationSendResult[];
  /** Suppliers that already have a sent PC request — hide remove (X). */
  nonRemovableSupplierIds?: string[];
}

const statusChip = (
  result: PartConfirmationSendResult | undefined,
): { label: string; colorScheme: string } | null => {
  if (!result) return null;
  if (result.status === "sent") {
    return { label: "Sent", colorScheme: "green" };
  }
  if (result.status === "created_without_notify") {
    return { label: "Created (no notify)", colorScheme: "orange" };
  }
  return { label: "Failed", colorScheme: "red" };
};

/**
 * Part Confirmation bucket — staged eligible suppliers for batch send.
 * User can remove suppliers that have not been sent yet.
 */
export const PartConfirmationBucketPanel = ({
  partConfirmationBucket,
  onRemoveSupplier,
  onSend,
  isSending,
  isSendEnabled = true,
  sendDisabledReason = "No new quantity to send",
  sendResults,
  nonRemovableSupplierIds = [],
}: PartConfirmationBucketPanelProps) => {
  if (partConfirmationBucket.length === 0) {
    return null;
  }

  const resultBySupplierId = new Map(
    (sendResults ?? []).map((result) => [result.supplierId, result]),
  );
  const lockedSupplierIds = new Set(
    nonRemovableSupplierIds.map((id) => String(id)),
  );

  const totalParts = partConfirmationBucket.reduce(
    (sum, entry) => sum + entry.confirmationParts.length,
    0,
  );
  const isSendDisabled = isSending || !isSendEnabled;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      p={4}
      boxShadow="md"
      border="1px solid"
      borderColor="gray.200"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Flex align="center" gap={2}>
          <BsFillInboxesFill color="#2966eb" />
          <Text
            fontSize="1rem"
            fontWeight="bold"
            color="gray.800"
            fontFamily="'Nunito','Inter', sans-serif"
          >
            Part Confirmation ({partConfirmationBucket.length})
          </Text>
        </Flex>
        <Text
          fontSize="0.7rem"
          color="gray.500"
          fontFamily="'Nunito','Inter', sans-serif"
        >
          {totalParts} part{totalParts === 1 ? "" : "s"}
        </Text>
      </Flex>

      <VStack
        spacing={3}
        align="stretch"
        maxH="27vh"
        overflowY="auto"
        width="100%"
        overflowX="hidden"
      >
        {partConfirmationBucket.map((entry, index) => {
          const chip = statusChip(resultBySupplierId.get(entry.supplierId));
          const canRemove =
            !lockedSupplierIds.has(String(entry.supplierId)) &&
            resultBySupplierId.get(entry.supplierId)?.status !== "sent";
          return (
            <Box
              key={entry.supplierId}
              bg="#F0F6FF"
              borderRadius="lg"
              p={3}
              position="relative"
            >
              {canRemove && (
                <IconButton
                  aria-label="Remove supplier from part confirmation bucket"
                  icon={<MdClose size="15" />}
                  variant="ghost"
                  colorScheme="red"
                  position="absolute"
                  top={1}
                  right={1}
                  size="2xs"
                  isDisabled={isSending}
                  cursor={isSending ? "not-allowed" : "pointer"}
                  onClick={() => onRemoveSupplier(entry.supplierId)}
                  _hover={{ bg: "transparent" }}
                />
              )}
              <Flex
                justify="space-between"
                align="center"
                width="100%"
                pr={canRemove ? 5 : 0}
              >
                <VStack align="start" spacing={0} flex={1}>
                  <Text
                    fontSize="0.875rem"
                    fontWeight="bold"
                    color="gray.800"
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    {entry.supplierName}
                  </Text>
                  <Text
                    fontSize="0.7rem"
                    color="gray.600"
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    {entry.confirmationParts.length} part
                    {entry.confirmationParts.length === 1 ? "" : "s"}
                  </Text>
                </VStack>
                {chip ? (
                  <Badge
                    colorScheme={chip.colorScheme}
                    borderRadius="full"
                    fontSize="0.65rem"
                    px={2}
                  >
                    {chip.label}
                  </Badge>
                ) : (
                  <Badge
                    bg="transparent"
                    color="#3B82F6"
                    borderRadius="full"
                    fontSize="0.9rem"
                    fontWeight="bold"
                  >
                    #{index + 1}
                  </Badge>
                )}
              </Flex>
            </Box>
          );
        })}
      </VStack>

      <Tooltip
        label={!isSendEnabled ? sendDisabledReason : ""}
        isDisabled={isSendEnabled}
        hasArrow
        placement="top"
        bg="gray.700"
        color="white"
        fontSize="sm"
      >
        <Button
          width="100%"
          mt={3}
          colorScheme="purple"
          variant="solid"
          size="sm"
          fontSize="sm"
          leftIcon={<FiSend size={16} />}
          isLoading={isSending}
          loadingText="Sending…"
          isDisabled={isSendDisabled}
          cursor={isSendDisabled ? "not-allowed" : "pointer"}
          _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
          onClick={onSend}
        >
          Part Confirmation
        </Button>
      </Tooltip>
    </Box>
  );
};
